import * as React from 'react';
import classNames from 'classnames';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useTranslation} from 'react-i18next';
import * as dragImage from '../../Images/drag-prompt-icon.png';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';

export enum DragToRotateStatus {
  NotShown,
  Pending,
  Show,
  Dismissed,
}

interface DragToRotateProps {
  threeDContainerId: string;
  status: DragToRotateStatus;
  isFullScreen: boolean;
  onStatusChange: (newStatus: DragToRotateStatus) => void;
}

/**
 * Component to show a drag to rotate message.
 *
 * @param props Lits of props required.
 */
const DragToRotate = ({
  status = DragToRotateStatus.NotShown,
  ...props
}: DragToRotateProps): JSX.Element => {
  const [dragToRotateStatus, setDragToRotateStatus] = React.useState<DragToRotateStatus>(status);
  const showTimeout: React.MutableRefObject<number> = React.useRef<number>(0);

  const ui = useUiState();
  const userFlow = useUserFlowState();
  const {t} = useTranslation();

  React.useEffect(() => {
    document
      .getElementById(props.threeDContainerId)
      ?.addEventListener('pointermove', onPointerMove);

    return () => {
      document
        .getElementById(props.threeDContainerId)
        ?.removeEventListener('pointermove', onPointerMove);
    };
  });

  React.useEffect(() => {
    switch (dragToRotateStatus) {
      // Start timer to show the popup.
      case DragToRotateStatus.Pending: {
        showDelayedPopup();
        break;
      }
      default: {
        break;
      }
    }

    props.onStatusChange(dragToRotateStatus);
  }, [dragToRotateStatus, userFlow.getStateStage()]);

  /**
   * Actiosn to execute on pointerMove event.
   *
   * @param event The event object.
   */
  const onPointerMove = (event: PointerEvent) => {
    if ('mouse' === event.pointerType) {
      // If event if fired and buttons is greater than 0 then we know the drag was performed.
      if (event.buttons > 0) {
        hide3DDragToRotatePopup();
      }
    } else {
      // Touch event.
      hide3DDragToRotatePopup();
    }
  };

  /**
   * Add timer to display the popup if not cancel during the execution time.
   */
  const showDelayedPopup = (): void => {
    if (showTimeout.current) {
      clearTimeout(showTimeout.current);
    }

    // Delay the load for 5 secs.
    showTimeout.current = window.setTimeout(() => {
      // Check if the message can be shown.
      display3DDragToRotatePopup();
    }, 3000);
  };

  /**
   * Checks if the tip popup can be shown.
   *
   * @returns True if the popup can be shown.
   */
  const canShow = (): boolean => {
    if (ui.state.busy) {
      return false;
    }

    if (userFlow.isUserFlowActive(UserFlowType.Assistant)) {
      return true;
    }

    if (!userFlow.getCurrentStep().includes(UserFlowStep.None)) {
      return false;
    }

    if (!document.getElementById('threedpreview')?.querySelector('canvas')) {
      return false;
    }

    return true;
  };

  /**
   * Show the drag to rotate message if able to be shown.
   */
  const display3DDragToRotatePopup = (): void => {
    // Wait until no popups/dialog windows are open.
    if (canShow()) {
      // Flag the popup has been shown.
      flagPopupShow();
    } else {
      // Set timer to try in another 500 mili sec.
      showDelayedPopup();
    }
  };

  /**
   * Close the drag to rotate message.
   */
  const hide3DDragToRotatePopup = (): void => {
    // Make sure it's not already closed.
    if (DragToRotateStatus.Dismissed !== dragToRotateStatus) {
      // Cancel timeout.
      if (showTimeout.current) {
        clearTimeout(showTimeout.current);
      }

      // Flag the popup has been dismissed.
      flagPopupDismissed();
    }
  };

  /**
   * Flag the popup to be shown.
   */
  const flagPopupShow = (): void => {
    setDragToRotateStatus(DragToRotateStatus.Show);
  };

  /**
   * Flag the popup has been dismissed by the user.
   */
  const flagPopupDismissed = (): void => {
    setDragToRotateStatus(DragToRotateStatus.Dismissed);
  };

  // If the popup has been displayed already or cancelled return nothing.
  if (DragToRotateStatus.Show !== dragToRotateStatus) {
    return <></>;
  }

  const wrapperClasses = classNames(
    dragToRotateStatus === DragToRotateStatus.Show ? 'animate-[fadeIn_easy_3s]' : 'hidden',
    'flex absolute top-0 left-0 z-[25] h-full w-full justify-center items-center pointer-events-none'
  );

  const messageTextClasses = classNames(
    'rounded-[10rem] border-solid border-2 border-white bg-black text-white text-center px-[15rem] pointer-events-none',
    props.isFullScreen
      ? 'pt-[100rem] pb-[15rem] px-[20rem] bg-no-repeat bg-[length:40%] bg-[center_18rem] w-[178rem]'
      : 'py-[8rem] w-auto'
  );

  return (
    <div id="threed-drag-to-rotate-message" className={wrapperClasses}>
      <div
        id="threed-drag-to-rotate-message-text"
        className={messageTextClasses}
        style={{backgroundImage: props.isFullScreen ? `url(${dragImage.default})` : 'none'}}
      >
        {t('str_LabelDragToRotate')}
      </div>
    </div>
  );
};

export default DragToRotate;
